/* eslint-disable @typescript-eslint/no-explicit-any */
import { MutableRefObject, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';

import { useDeleteConversation } from '@/hooks/useChat';
import { getConversationModes } from '@/sections/insights/aiAssistant/config/storage';
import { useAutoDrawerStore } from '@/sections/insights/aiAssistant/stores/autoDrawer.store';
import type {
  AIAssistantMode,
  SidebarTab,
} from '@/sections/insights/aiAssistant/types';
import { extractConversationId } from '@/sections/insights/aiAssistant/utils/conversationId';

interface UseConversationManagerProps {
  conversations: any[];
  messages: any[];
  conversationId: string | null;
  /** Deliberate chat-mode write-through (state + persist). Owned by AIAssistant. */
  applyChatMode: (mode: 'query' | 'agent') => void;
  setActiveTab: (tab: SidebarTab) => void;
  /** Toggle the orthogonal hunt ('auto') app-view flag. Owned by AIAssistant. */
  setIsHuntView: (isHuntView: boolean) => void;
  /** Optional: called by handleCreateConversation to clear waiting state. */
  setIsWaitingForResponse?: (waiting: boolean) => void;
  isInputDisabled: boolean;
  /**
   * modeChangeRef is owned by AIAssistant.tsx (so the same ref backs both
   * convState derivation and the switch effect) and threaded in here.
   */
  modeChangeRef: MutableRefObject<'auto' | null>;
}

export function useConversationManager({
  conversations,
  messages,
  conversationId,
  applyChatMode,
  setActiveTab,
  setIsHuntView,
  setIsWaitingForResponse,
  isInputDisabled,
  modeChangeRef,
}: UseConversationManagerProps) {
  const [, setSearchParams] = useSearchParams();
  const [deletedConversationIds, setDeletedConversationIds] = useState<
    Set<string>
  >(new Set());

  const resetAutoDrawer = useAutoDrawerStore(s => s.reset);

  const deleteConversation = useDeleteConversation();

  const handleModeChange = (newMode: AIAssistantMode) => {
    if (newMode === 'auto') {
      // Hunt app-view: orthogonal flag, never a per-conversation mode.
      setIsHuntView(true);
      setActiveTab('hunts');
      modeChangeRef.current = 'auto';
      resetAutoDrawer();
      setSearchParams(prev => {
        const newParams = new URLSearchParams(prev);
        newParams.delete('conversationId');
        return newParams;
      });
    } else {
      // applyChatMode already clears isHuntView and persists by id.
      applyChatMode(newMode);
      setActiveTab('conversations');
      modeChangeRef.current = null;
      resetAutoDrawer();
    }
  };

  const handleCreateConversation = () => {
    if (isInputDisabled) return;

    setIsWaitingForResponse?.(false);
    // Leaving any current conversation for a fresh chat: clear the hunt view.
    // The cleared conversationId makes deriveConvState yield {null,'query',null}
    // automatically — no setMode/setActiveAgent needed.
    setIsHuntView(false);
    // From Hunt view conversationId is already null, so the setSearchParams
    // delete is a no-op and the switch effect never fires to reset the tab —
    // set it here so the sidebar leaves the 'hunts' tab for the fresh chat.
    setActiveTab('conversations');
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      newParams.delete('conversationId');
      return newParams;
    });
  };

  const handleSelectConversation = (id: string) => {
    // A selected sidebar conversation is always a chat conversation: hunt
    // conversations are excluded from the list by isSidebarConversation
    // (useChatWorker), so selecting one always leaves the hunt view. The mode
    // for `id` derives from the new conversationId (deriveConvState heals a
    // stale 'auto' to 'query') — there is no setMode here.
    setIsHuntView(false);
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      newParams.set('conversationId', id);
      return newParams;
    });
    useAutoDrawerStore.getState().reset();
  };

  const handleDeleteConversation = (id: string) => {
    // Optimistically hide it from the list immediately
    setDeletedConversationIds(prev => new Set(prev).add(id));

    deleteConversation.mutate(id, {
      onSuccess: () => {
        if (conversationId === id) {
          setSearchParams(prev => {
            const newParams = new URLSearchParams(prev);
            newParams.delete('conversationId');
            return newParams;
          });
        }
      },
      onError: () => {
        // Rollback if the API call fails
        setDeletedConversationIds(prev => {
          const next = new Set(prev);
          next.delete(id);
          return next;
        });
      },
    });
  };

  // Filter out optimistically deleted conversations
  const visibleConversations = useMemo(
    () =>
      conversations.filter((c: any) => {
        const id = extractConversationId(c);
        return !deletedConversationIds.has(id);
      }),
    [conversations, deletedConversationIds]
  );

  // Pre-compute lightweight preview map for ConversationList (prevents DataCloneError)
  const conversationPreviews = useMemo(() => {
    const previews: Record<string, string> = {};
    for (const conv of conversations) {
      const id = extractConversationId(conv);
      // Already has a title from the backend? Skip message scanning.
      if (conv.name || conv.title || conv.topic) continue;

      // Find first user message for this conversation
      const firstUserMsg = messages.find(
        (msg: any) =>
          msg.role === 'user' &&
          (msg.key?.includes(id) || msg.conversationId === id)
      );
      if (firstUserMsg?.content) {
        const words = firstUserMsg.content
          .trim()
          .split(' ')
          .slice(0, 6)
          .join(' ');
        previews[id] =
          words.length > 40 ? words.substring(0, 40) + '...' : words;
      }
    }
    return previews;
  }, [conversations, messages]);

  const modeFilteredConversations = useMemo(() => {
    const allModes = getConversationModes(); // read localStorage once per render
    return visibleConversations.filter((conv: any) => {
      const convId = extractConversationId(conv);
      const savedMode = allModes[convId]?.mode ?? 'query';
      return savedMode !== 'auto';
    });
  }, [visibleConversations]);

  return {
    visibleConversations,
    conversationPreviews,
    modeFilteredConversations,
    handleDeleteConversation,
    handleModeChange,
    handleCreateConversation,
    handleSelectConversation,
  };
}
